import {
  forwardRef, useCallback, useMemo,
  useRef,
} from 'react';
import { useDispatch } from 'react-redux';
import { guaranteeHash } from '~/_shared/components/colorPicker/colorPicker.helpers';
import { type ColorResult } from '~/_shared/components/colorPicker/colorPicker.types';
import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useIsDataExportAllowed } from '~/_shared/types/toolsAndFeatures/useIsDataExportAllowed';
import { getBoundingBox } from '~/_shared/utils/gis/boundingBox.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNull } from '~/_shared/utils/typeGuards';
import { useModal } from '~/modal/useModal.hook';
import { mapComponentSetZoomToBounds } from '~/store/frontendState/mapComponent/mapComponent.actionCreators';
import {
  frontendStateDirectionsScrollIntoViewpointsForm,
  frontendStateDirectionsSetWaypoints,
  routeCollapsed,
  routeExpanded,
  routeLegCollapsed,
  routeLegExpanded,
} from '~/store/frontendState/mapTools/directions/directions.actionCreators';
import {
  useDirectionsExpandedRouteIdsSelector, useRoutesUiDataSelector,
} from '~/store/frontendState/mapTools/directions/directions.selectors';
import {
  modifyRouteInDirections,
  removeRouteFromDirections,
} from '~/store/mapSettings/directions/mapSettingsDirections.actionCreators';
import { useMapSettingsDirectionsRoutesSelector } from '~/store/mapSettings/directions/mapSettingsDirections.selectors';
import {
  type MapSettingsRoute, type RouteId,
} from '~/store/mapSettings/directions/mapSettingsDirections.state';
import {
  toolStateHideRoute, toolStateShowRoute,
} from '~/store/mapSettings/toolsState/visibility/visibility.actionCreators';
import { useHiddenRouteIdsSelector } from '~/store/mapSettings/toolsState/visibility/visibility.selectors';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { getRouteUiDataForWaypoint } from '../directions.helper';
import { DirectionsListingComponent } from './directionsListing.component';

type DirectionsListingContainer = Readonly<{
  isPrintVersion?: boolean;
}>;

export const DirectionsListingContainer = forwardRef<HTMLDivElement, DirectionsListingContainer>((props, forwardedRef) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const mapId = useMapIdSelector();
  const expandedRouteIds = useDirectionsExpandedRouteIdsSelector();
  const routes = useMapSettingsDirectionsRoutesSelector();
  const routesUiData = useRoutesUiDataSelector();
  const hiddenRoutes = useHiddenRouteIdsSelector();
  const { openModal: openExportModal } = useModal('ExportDirections');
  const isDataExportAllowed = useIsDataExportAllowed(MapTool.Routing);

  const hiddenRoutesIds = useMemo(() => new Set(hiddenRoutes), [hiddenRoutes]);

  const collapseLeg = useCallback((routeId: RouteId, legIndex: number) => {
    dispatch(routeLegCollapsed(routeId, legIndex));
  }, [dispatch]);

  const expandLeg = useCallback((routeId: RouteId, legIndex: number) => {
    dispatch(routeLegExpanded(routeId, legIndex));
  }, [dispatch]);

  const collapseRoute = useCallback((routeId: RouteId) => {
    dispatch(routeCollapsed(routeId));
  }, [dispatch]);

  const expandRoute = useCallback((routeId: RouteId) => {
    dispatch(routeExpanded(routeId));
  }, [dispatch]);

  const hideRoute = useCallback((routeId: RouteId) => {
    dispatch(toolStateHideRoute(routeId));
  }, [dispatch]);

  const showRoute = useCallback((routeId: RouteId) => {
    dispatch(toolStateShowRoute(routeId));
  }, [dispatch]);

  const loadDirections = useCallback((routeId: RouteId) => {
    const selectedRoute = routes.find(r => r.id === routeId);
    const waypoints = selectedRoute?.waypoints;
    if (!waypoints) {
      return;
    }

    dispatch(frontendStateDirectionsSetWaypoints(waypoints, false, selectedRoute.id));
    dispatch(frontendStateDirectionsScrollIntoViewpointsForm());
  }, [dispatch, routes]);

  const updateRouteDetails = useCallback((routeId: string, name: string, color: ColorResult) => {
    const route = routes.find(r => r.id === routeId);
    if (route) {
      dispatch(modifyRouteInDirections(routeId, { ...route, name, color: guaranteeHash(color.hex) }));
    }
  }, [dispatch, routes]);

  const removeRoute = useMemo(() => {
    if (!mapId) {
      return undefined;
    }
    return (route: MapSettingsRoute) => dispatch(removeRouteFromDirections(route, mapId));
  }, [dispatch, mapId]);

  const routesUiDataRef = useRef(routesUiData);
  const zoomToRoute = useCallback((routeId: string) => {
    const selectedRoute = routes.find(r => r.id === routeId);
    const waypointLatLngs = selectedRoute?.waypoints.map((waypoint, waypointIndex) => {
      if (waypoint.latLng) {
        return waypoint.latLng;
      }

      const routeUiLatLng = getRouteUiDataForWaypoint(waypointIndex, routeId, routesUiDataRef.current, t)?.latLng;
      if (routeUiLatLng) {
        return routeUiLatLng;
      }

      return null;
    }).filter(notNull);

    if (!waypointLatLngs) {
      return;
    }
    const boundingBoxAroundWaypoints = getBoundingBox(waypointLatLngs);

    dispatch(mapComponentSetZoomToBounds(boundingBoxAroundWaypoints));
  }, [dispatch, routes, t]);

  const allowExportLocations = isDataExportAllowed && !props.isPrintVersion;

  const onRouteExport = useCallback((routeId: string) => {
    const route = routes.find(route => route.id === routeId);

    if (!route) {
      return;
    }

    openExportModal({
      showSelectRoutesSection: false,
      selectedRouteIds: [routeId],
    });
  }, [openExportModal, routes]);

  return (
    <DirectionsListingComponent
      ref={forwardedRef}
      isPrintVersion={props.isPrintVersion}
      expandedRouteIds={expandedRouteIds}
      hiddenRouteIds={hiddenRoutesIds}
      collapseLeg={collapseLeg}
      expandLeg={expandLeg}
      onHideRoute={hideRoute}
      onShowRoute={showRoute}
      onLoadDirections={loadDirections}
      onRouteEdit={updateRouteDetails}
      onRouteRemove={removeRoute}
      collapseRoute={collapseRoute}
      expandRoute={expandRoute}
      onZoomToRoute={zoomToRoute}
      routes={routes}
      routesUiData={routesUiData}
      unitSystem={UnitSystem.imperial}
      onRouteExport={allowExportLocations ? onRouteExport : undefined}
    />
  );
});
